import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Plus, Trash2, Building2, Layers, Check, Zap } from "lucide-react"
import { useToast } from "src/components/ui/toast"
import {
  fetchProjects, fetchTowers, fetchUnits, configureUnitClp, applyTowerClp,
} from "src/features/clp/api/clpApi"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"

const TRIGGERS = ["Booking", "Agreement", "Construction", "Finishing", "Possession"]

const DEFAULT_MILESTONES = [
  { name: "On booking", trigger: "Booking", percent: 10, due_offset_days: 0 },
  { name: "On agreement", trigger: "Agreement", percent: 15, due_offset_days: 30 },
  { name: "Plinth completion", trigger: "Construction", percent: 15, due_offset_days: 120 },
  { name: "Slabs 1–10 cast", trigger: "Construction", percent: 30, due_offset_days: 240 },
  { name: "Brickwork done", trigger: "Construction", percent: 10, due_offset_days: 300 },
  { name: "Flooring done", trigger: "Finishing", percent: 10, due_offset_days: 360 },
  { name: "On possession", trigger: "Possession", percent: 10, due_offset_days: 540 },
]

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0)

function planBadge(hasPlan) {
  if (hasPlan) return { label: "Custom", cls: "bg-violet-100 text-violet-700" }
  return { label: "Not set", cls: "bg-amber-100 text-amber-700" }
}

export default function PaymentPlansPage() {
  const { toast } = useToast()
  const [projects, setProjects] = useState([])
  const [projectId, setProjectId] = useState("")
  const [towers, setTowers] = useState([])
  const [towerId, setTowerId] = useState("")
  const [units, setUnits] = useState([])
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [milestones, setMilestones] = useState(DEFAULT_MILESTONES)
  const [planName, setPlanName] = useState("Construction-linked")
  const [saving, setSaving] = useState(false)
  const [showApply, setShowApply] = useState(false)

  useEffect(() => {
    fetchProjects().then((d) => {
      const list = d.projects || d || []
      setProjects(list)
      if (list[0]) setProjectId(String(list[0].id))
    }).catch(() => {})
  }, [])

  const loadInventory = useCallback(async (pid) => {
    if (!pid) return
    const [tw, un] = await Promise.all([fetchTowers(pid), fetchUnits(pid)])
    setTowers(tw.towers || [])
    setUnits(un.units || [])
  }, [])

  useEffect(() => {
    if (projectId) loadInventory(projectId)
  }, [projectId, loadInventory])

  const visibleUnits = useMemo(
    () => (towerId ? units.filter((u) => String(u.tower_id) === String(towerId)) : units),
    [units, towerId]
  )

  function selectUnit(u) {
    setSelectedUnit(u)
    const existing = u.payment_plan?.milestones
    setMilestones(existing?.length ? existing : DEFAULT_MILESTONES)
    setPlanName(u.payment_plan?.plan_name || "Construction-linked")
  }

  const totalPct = milestones.reduce((s, m) => s + Number(m.percent || 0), 0)
  const agreementValue = selectedUnit?.base_price || 0

  function updateRow(i, field, value) {
    setMilestones((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)))
  }
  function addRow() {
    setMilestones((rows) => [...rows, { name: "", trigger: "Construction", percent: 0, due_offset_days: 0 }])
  }
  function removeRow(i) {
    setMilestones((rows) => rows.filter((_, idx) => idx !== i))
  }

  async function save() {
    if (!selectedUnit) return
    if (Math.round(totalPct) !== 100) {
      toast({ variant: "destructive", title: "Percentages must total 100%", description: `Currently ${totalPct}%` })
      return
    }
    setSaving(true)
    try {
      const res = await configureUnitClp(projectId, selectedUnit.id, { milestones, plan_name: planName })
      setUnits((list) => list.map((u) => (u.id === selectedUnit.id ? res.unit : u)))
      setSelectedUnit(res.unit)
      toast({ title: "Plan saved", description: `Unit ${selectedUnit.unit_number}` })
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: err.response?.data?.errors?.[0] || "Error" })
    } finally {
      setSaving(false)
    }
  }

  async function apply(mode) {
    setShowApply(false)
    if (Math.round(totalPct) !== 100) {
      toast({ variant: "destructive", title: "Percentages must total 100%" })
      return
    }
    try {
      const res = await applyTowerClp(projectId, towerId, { milestones, mode, plan_name: planName })
      await loadInventory(projectId)
      toast({ title: "Applied to tower", description: `${res.applied} updated, ${res.skipped} skipped (${mode === "overwrite" ? "overwrote custom" : "filled blanks"})` })
    } catch (err) {
      toast({ variant: "destructive", title: "Could not apply", description: err.response?.data?.errors?.[0] || "Error" })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">Construction-linked plans</h2>
        <p className="mt-1 text-sm text-slate-500">Configure a payment schedule per unit. Apply one plan across a whole tower when units share terms.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Selector icon={Building2} value={projectId} onChange={(v) => { setProjectId(v); setTowerId(""); setSelectedUnit(null) }}
          options={projects.map((p) => ({ value: String(p.id), label: p.name }))} placeholder="Project" />
        <Selector icon={Layers} value={towerId} onChange={(v) => setTowerId(v)}
          options={[{ value: "", label: "All towers" }, ...towers.map((t) => ({ value: String(t.id), label: t.name }))]} placeholder="Tower" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        <div>
          <div className="mb-2 pl-1 text-xs text-slate-400">Units{towerId ? "" : " (all towers)"}</div>
          <div className="flex flex-col gap-1.5">
            {visibleUnits.map((u) => {
              const b = planBadge(u.has_payment_plan)
              const active = selectedUnit?.id === u.id
              return (
                <button key={u.id} onClick={() => selectUnit(u)}
                  className={`rounded-xl border px-3 py-2 text-left transition ${active ? "border-blue-400 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">{u.unit_number}</span>
                    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${b.cls}`}>{b.label}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">{u.unit_type || "—"} · {inr(u.base_price)}</div>
                </button>
              )
            })}
            {visibleUnits.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400">No units</div>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          {!selectedUnit ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-400">Select a unit to configure its plan</div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-base font-semibold text-slate-900">Unit {selectedUnit.unit_number} · {selectedUnit.unit_type || "—"}</div>
                  <div className="text-sm text-slate-500">Agreement value {inr(agreementValue)}</div>
                </div>
                <input value={planName} onChange={(e) => setPlanName(e.target.value)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700" placeholder="Plan name" />
              </div>

              <div className="mt-4 grid grid-cols-[1fr_120px_70px_120px_32px] gap-2 px-1 pb-2 text-[11px] text-slate-400">
                <span>Milestone</span><span>Trigger</span><span className="text-right">%</span><span className="text-right">Amount</span><span />
              </div>
              <div className="flex flex-col gap-1.5">
                {milestones.map((m, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_70px_120px_32px] items-center gap-2">
                    <input value={m.name} onChange={(e) => updateRow(i, "name", e.target.value)}
                      className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" placeholder="Milestone" />
                    <Select
                      value={m.trigger}
                      onValueChange={(val) => updateRow(i, "trigger", val)}
                    >
                      <SelectTrigger className="w-full h-8 text-xs text-slate-600 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRIGGERS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input type="number" value={m.percent} onChange={(e) => updateRow(i, "percent", Number(e.target.value))}
                      className="rounded-lg border border-slate-200 px-2 py-1.5 text-right text-sm" />
                    <div className="text-right text-sm text-slate-500">{inr((agreementValue * Number(m.percent || 0)) / 100)}</div>
                    <button onClick={() => removeRow(i)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-3 grid grid-cols-[1fr_120px_70px_120px_32px] gap-2 border-t border-slate-200 pt-3 text-sm font-medium">
                <span>Total</span><span />
                <span className={`text-right ${Math.round(totalPct) === 100 ? "text-green-600" : "text-red-500"}`}>{totalPct}%</span>
                <span className="text-right">{inr(agreementValue)}</span><span />
              </div>

              <div className="mt-4 flex items-center gap-2">
                <button onClick={addRow} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Plus className="h-3.5 w-3.5" /> Add milestone
                </button>
                <span className="flex-1" />
                {towerId && (
                  <button onClick={() => setShowApply(true)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                    Apply to whole tower
                  </button>
                )}
                <button onClick={save} disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50">
                  {saving ? <Zap className="h-3.5 w-3.5 animate-pulse" /> : <Check className="h-3.5 w-3.5" />} Save plan
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showApply && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setShowApply(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900">Apply this plan to every unit in the tower?</h3>
            <p className="mt-1 text-sm text-slate-500">Choose what happens to units that already have a custom plan.</p>
            <div className="mt-4 flex flex-col gap-2">
              <button onClick={() => apply("fill_only")} className="rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-slate-300">
                <div className="text-sm font-medium text-slate-900">Only fill units with no plan</div>
                <div className="text-xs text-slate-500">Existing custom plans are kept.</div>
              </button>
              <button onClick={() => apply("overwrite")} className="rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-red-300">
                <div className="text-sm font-medium text-slate-900">Overwrite all units</div>
                <div className="text-xs text-slate-500">Replaces every unit's plan, including custom ones.</div>
              </button>
            </div>
            <button onClick={() => setShowApply(false)} className="mt-3 w-full rounded-lg px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

function Selector({ icon: Icon, value, onChange, options, placeholder }) {
  const hasEmptyOption = options.some((o) => o.value === "")
  const selectValue = value ? String(value) : (hasEmptyOption ? "__empty" : "")

  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1 bg-white">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <Select
        value={selectValue}
        onValueChange={(val) => onChange(val === "__empty" ? "" : val)}
      >
        <SelectTrigger className="border-0 p-0 h-8 w-[150px] shadow-none focus:ring-0 focus:ring-offset-0 bg-transparent text-slate-700 text-sm font-medium">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value || "__empty"} value={o.value ? String(o.value) : "__empty"}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
